import { useState, type ReactNode } from "react";

// The actual popup bubble — same surface3/line2/pop combo PriceChart/
// PnlChart's own marker-hover tooltips already use. Split out from HoverTip
// below so KpiCard (which needs hover state on a big flex-sized card div,
// not a small inline span) can render the exact same bubble without an
// extra wrapper element fighting its own flex sizing. No explicit width —
// shrinks to fit short content (a formula, a short list of rows) and wraps
// naturally if a caller ever hands it something long.
export function TooltipBubble({ content, placement = "top" }: { content: ReactNode; placement?: "top" | "bottom" }) {
  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        // "top" (opens upward, anchored above the trigger) is right for
        // mid-page triggers (Комиссия/Итоговый P&L in Trades' detail
        // panel) but has nowhere to open for a trigger sitting at the very
        // top of the viewport — AppShell's header Баланс widget, reported
        // live as "просто не видно" (clipped off-screen above the page).
        // "bottom" opens downward instead for exactly that case.
        ...(placement === "top" ? { bottom: "calc(100% + 8px)" } : { top: "calc(100% + 8px)" }),
        transform: "translateX(-50%)",
        padding: "9px 11px",
        borderRadius: 10,
        background: "var(--surface3)",
        border: "1px solid var(--line2)",
        boxShadow: "var(--pop)",
        font: "400 11.5px 'JetBrains Mono',monospace",
        color: "var(--fg)",
        zIndex: 60,
        pointerEvents: "none",
      }}
    >
      {content}
    </div>
  );
}

interface HoverTipProps {
  content: ReactNode;
  children: ReactNode;
  // "top" (default) fits DetailRow's own mid-page triggers. A trigger
  // sitting near the top of the viewport (Overview's compact open-position
  // rows, right under the KPI card) needs "bottom" instead — same reasoning
  // as TooltipBubble's own placement comment (AppShell header widget clipped
  // off-screen opening upward).
  placement?: "top" | "bottom";
}

// Small reusable hover tooltip wrapper for inline content (a label, a
// value) — user request: DetailRow's plain inline text was unreadable
// crammed next to a number ("Комиссия −$2,46 (вход $1,23 + выход $1,24)"),
// wanted the breakdown moved into "приличная подсказка в том же стиле"
// instead. Anchored above the trigger and horizontally centered — good
// enough for the short content this is meant for; not a general-purpose
// positioning system (no viewport-edge flipping).
export function HoverTip({ content, children, placement = "top" }: HoverTipProps) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", cursor: "help", borderBottom: "1px dotted var(--fg3)" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && <TooltipBubble content={content} placement={placement} />}
    </span>
  );
}
